// Imposter Host Page
// Main game display and controls for the host

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { ImposterStateDocument, getPlayerView } from '../games/imposter/logic';
import {
  ImposterPlayerRecord,
  subscribeToImposterPlayers,
  subscribeToImposterGameState,
  resetPlayerRoundStatus,
} from '../games/imposter/lobby';
import {
  startImposterGame,
  startCluePhase,
  submitVote,
  submitImposterGuess,
  revealAndScore,
  forceVoting,
  forceNextRound,
  startNewRound,
  endImposterGame,
} from '../games/imposter/service';

const safeOrigin = typeof window !== 'undefined' 
  ? window.location.origin 
  : 'https://qroke-a6fed.web.app';

const ImposterHostPage: React.FC = () => {
  const { partyId } = useParams<{ partyId: string }>();
  const navigate = useNavigate();
  
  const [gameState, setGameState] = useState<ImposterStateDocument | null>(null);
  const [players, setPlayers] = useState<ImposterPlayerRecord[]>([]);
  const [status, setStatus] = useState('Waiting for players to join...');
  const [error, setError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [hostGuess, setHostGuess] = useState('');

  const joinUrl = partyId ? `${safeOrigin}/party/${partyId}/game/imposter/join` : '';

  // Subscribe to players
  useEffect(() => {
    if (!partyId) return;
    const unsubscribe = subscribeToImposterPlayers(partyId, setPlayers);
    return () => unsubscribe();
  }, [partyId]);

  // Subscribe to game state
  useEffect(() => {
    if (!partyId) return;
    const unsubscribe = subscribeToImposterGameState(partyId, setGameState);
    return () => unsubscribe();
  }, [partyId]);

  // Update status based on game state
  useEffect(() => {
    if (!gameState) {
      setStatus('Waiting for players to join...');
    } else if (gameState.phase === 'dealing') {
      setStatus('Dealing roles... Start when ready!');
    } else if (gameState.phase === 'clues') {
      const currentPlayer = gameState.players[gameState.currentPlayerIndex];
      setStatus(`Waiting for ${currentPlayer} to give a clue (Round ${gameState.currentRound})`);
    } else if (gameState.phase === 'voting') {
      setStatus('Voting phase - who is the imposter?');
    } else if (gameState.phase === 'reveal') {
      setStatus('Ready to reveal results!');
    } else if (gameState.phase === 'finished') {
      setStatus('Round finished - ready for next round?');
    }
  }, [gameState]);

  const waitingPlayers = useMemo(
    () => players.filter(p => p.status === 'waiting' || p.status === 'ready'),
    [players]
  );

  const playingPlayers = useMemo(
    () => players.filter(p => p.status === 'playing'),
    [players]
  );

  const handleStartGame = useCallback(async () => {
    if (!partyId || actionBusy || waitingPlayers.length < 3) return;
    
    setActionBusy(true);
    setError(null);
    
    try {
      const playerIds = waitingPlayers.map(p => p.name);
      await startImposterGame(partyId, playerIds);
      setStatus('Game started! Dealing roles...');
    } catch (err: any) {
      setError(err.message || 'Failed to start game');
      console.error('Error starting game:', err);
    } finally {
      setActionBusy(false);
    }
  }, [partyId, waitingPlayers, actionBusy]);

  const handleStartClues = useCallback(async () => {
    if (!partyId || !gameState || actionBusy) return;
    
    setActionBusy(true);
    setError(null);
    
    try {
      await startCluePhase(partyId);
      // Reset player flags when starting first clue round
      // Use actual player IDs from the players list, not names from gameState
      const playerIds = players.filter(p => p.status === 'playing').map(p => p.id);
      await resetPlayerRoundStatus(partyId, playerIds);
      setStatus('Clue phase started!');
    } catch (err: any) {
      setError(err.message || 'Failed to start clues');
      console.error('Error starting clues:', err);
    } finally {
      setActionBusy(false);
    }
  }, [partyId, gameState, players, actionBusy]);

  const handleSubmitImposterGuess = useCallback(async () => {
    if (!partyId || !gameState || !hostGuess.trim() || actionBusy) return;
    
    setActionBusy(true);
    setError(null);
    
    try {
      await submitImposterGuess(partyId, hostGuess.trim());
      setHostGuess('');
    } catch (err: any) {
      setError(err.message || 'Failed to submit guess');
      console.error('Error submitting guess:', err);
    } finally {
      setActionBusy(false);
    }
  }, [partyId, gameState, hostGuess, actionBusy]);

  const handleReveal = useCallback(async () => {
    if (!partyId || !gameState || actionBusy) return;
    
    setActionBusy(true);
    setError(null);
    
    try {
      await revealAndScore(partyId);
      setStatus('Results revealed!');
    } catch (err: any) {
      setError(err.message || 'Failed to reveal');
      console.error('Error revealing:', err);
    } finally {
      setActionBusy(false);
    }
  }, [partyId, gameState, actionBusy]);

  const handleNewRound = useCallback(async () => {
    if (!partyId || !gameState || actionBusy) return;
    
    setActionBusy(true);
    setError(null);
    
    try {
      await startNewRound(partyId);
      // Use actual player IDs from the players list, not names from gameState
      const playerIds = players.filter(p => p.status === 'playing').map(p => p.id);
      await resetPlayerRoundStatus(partyId, playerIds);
      setStatus('New round started!');
    } catch (err: any) {
      setError(err.message || 'Failed to start new round');
      console.error('Error starting new round:', err);
    } finally {
      setActionBusy(false);
    }
  }, [partyId, gameState, players, actionBusy]);

  const handleForceVoting = useCallback(async () => {
    if (!partyId || !gameState || actionBusy) return;
    
    setActionBusy(true);
    setError(null);
    
    try {
      await forceVoting(partyId);
      setStatus('Moving to voting phase...');
    } catch (err: any) {
      setError(err.message || 'Failed to force voting');
      console.error('Error forcing voting:', err);
    } finally {
      setActionBusy(false);
    }
  }, [partyId, gameState, actionBusy]);

  const handleForceNextRound = useCallback(async () => {
    if (!partyId || !gameState || actionBusy) return;
    
    setActionBusy(true);
    setError(null);
    
    try {
      await forceNextRound(partyId);
      setStatus(`Round ${gameState.currentRound + 1} started!`);
    } catch (err: any) {
      setError(err.message || 'Failed to advance round');
      console.error('Error forcing next round:', err);
    } finally {
      setActionBusy(false);
    }
  }, [partyId, gameState, actionBusy]);

  const handleEndGame = useCallback(async () => {
    if (!partyId || actionBusy) return;
    
    setActionBusy(true);
    setError(null);
    
    try {
      await endImposterGame(partyId);
      navigate(`/party/${partyId}`);
    } catch (err: any) {
      setError(err.message || 'Failed to end game');
      console.error('Error ending game:', err);
    } finally {
      setActionBusy(false);
    }
  }, [partyId, actionBusy, navigate]);

  // Get vote tallies
  const voteTallies = useMemo(() => {
    if (!gameState || gameState.phase !== 'reveal') return {};
    
    const tallies: Record<string, number> = {};
    gameState.votes.forEach(vote => {
      tallies[vote.suspectId] = (tallies[vote.suspectId] || 0) + 1;
    });
    return tallies;
  }, [gameState]);

  // Get sorted scores
  const sortedScores = useMemo(() => {
    if (!gameState) return [];
    
    return Object.entries(gameState.scores)
      .map(([name, score]) => ({ name, score }))
      .sort((a, b) => b.score - a.score);
  }, [gameState]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-900 text-white p-6">
      {/* Header */}
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-4xl font-bold text-purple-200">🎭 Imposter Game</h1>
            <p className="text-sm text-purple-300">{status}</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowQrModal(true)}
              className="bg-purple-600 hover:bg-purple-500 px-4 py-2 rounded-lg font-semibold"
            >
              📱 Show QR Code
            </button>
            <button
              onClick={() => navigate(`/party/${partyId}`)}
              className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg"
            >
              ← Back to Party
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-900/50 border border-red-500 rounded-lg p-4 mb-6">
            <p className="text-red-200">{error}</p>
          </div>
        )}

        {/* Main Game Area */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Players */}
          <div className="bg-white/10 rounded-xl p-6 border border-purple-400/30">
            <h2 className="text-2xl font-bold mb-4 text-purple-200">
              Players ({waitingPlayers.length + playingPlayers.length})
            </h2>
            
            <div className="space-y-2">
              {[...playingPlayers, ...waitingPlayers].map(player => (
                <div
                  key={player.id}
                  className="bg-purple-900/30 rounded-lg p-3 border border-purple-500/30"
                >
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">{player.name}</span>
                    <div className="flex gap-2">
                      {player.hasSubmittedClue && (
                        <span className="text-xs bg-green-500/30 px-2 py-1 rounded">
                          ✓ Clue
                        </span>
                      )}
                      {player.hasVoted && (
                        <span className="text-xs bg-blue-500/30 px-2 py-1 rounded">
                          ✓ Voted
                        </span>
                      )}
                      {player.isReady && (
                        <span className="text-xs bg-purple-500/30 px-2 py-1 rounded">
                          Ready
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {!gameState && (
              <button
                onClick={handleStartGame}
                disabled={actionBusy || waitingPlayers.length < 3}
                className="w-full mt-4 bg-green-600 hover:bg-green-500 disabled:bg-gray-600 disabled:cursor-not-allowed px-6 py-3 rounded-lg font-bold text-lg"
              >
                {waitingPlayers.length < 3
                  ? `Need ${3 - waitingPlayers.length} more players`
                  : 'Start Game'}
              </button>
            )}
          </div>

          {/* Center Column - Game State */}
          <div className="bg-white/10 rounded-xl p-6 border border-purple-400/30">
            <h2 className="text-2xl font-bold mb-4 text-purple-200">Game Info</h2>
            
            {gameState ? (
              <div className="space-y-4">
                <div className="bg-indigo-900/40 rounded-lg p-4 border border-indigo-400/30">
                  <p className="text-sm text-purple-300">Category</p>
                  <p className="text-2xl font-bold">{gameState.category}</p>
                </div>

                <div className="bg-indigo-900/40 rounded-lg p-4 border border-indigo-400/30">
                  <p className="text-sm text-purple-300">Secret Word</p>
                  <p className="text-3xl font-bold text-yellow-300">
                    {gameState.revealedImposters ? gameState.secretWord : '???'}
                  </p>
                </div>

                {gameState.revealedImposters && (
                  <div className="bg-red-900/40 rounded-lg p-4 border border-red-400/30">
                    <p className="text-sm text-red-300">Imposter(s)</p>
                    <div className="space-y-1">
                      {gameState.imposterIds.map(id => (
                        <p key={id} className="text-xl font-bold text-red-200">{id}</p>
                      ))}
                    </div>
                  </div>
                )}

                {gameState.phase === 'clues' && (
                  <div className="bg-blue-900/40 rounded-lg p-4 border border-blue-400/30">
                    <p className="text-sm text-blue-300">Round {gameState.currentRound}</p>
                    <p className="text-lg">
                      Current: {gameState.players[gameState.currentPlayerIndex]}
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="space-y-2 mt-4">
                  {gameState.phase === 'dealing' && (
                    <button
                      onClick={handleStartClues}
                      disabled={actionBusy}
                      className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-4 py-3 rounded-lg font-bold"
                    >
                      Start Clue Round
                    </button>
                  )}

                  {gameState.phase === 'clues' && (
                    <div className="space-y-2">
                      <button
                        onClick={handleForceNextRound}
                        disabled={actionBusy}
                        className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-4 py-3 rounded-lg font-bold"
                      >
                        ⏭️ Force Next Round
                      </button>
                      <button
                        onClick={handleForceVoting}
                        disabled={actionBusy}
                        className="w-full bg-orange-600 hover:bg-orange-500 disabled:bg-gray-600 px-4 py-3 rounded-lg font-bold"
                      >
                        ⚠️ Force Voting Phase
                      </button>
                    </div>
                  )}

                  {gameState.phase === 'reveal' && !gameState.imposterGuess && (
                    <div className="space-y-2">
                      <p className="text-sm text-purple-300">Imposter's Guess:</p>
                      <input
                        type="text"
                        value={hostGuess}
                        onChange={e => setHostGuess(e.target.value)}
                        placeholder="Enter imposter's guess..."
                        className="w-full bg-black/30 border border-purple-400/30 rounded-lg px-4 py-2 text-white"
                      />
                      <button
                        onClick={handleSubmitImposterGuess}
                        disabled={actionBusy || !hostGuess.trim()}
                        className="w-full bg-yellow-600 hover:bg-yellow-500 disabled:bg-gray-600 px-4 py-2 rounded-lg font-bold"
                      >
                        Submit Guess
                      </button>
                    </div>
                  )}

                  {gameState.phase === 'reveal' && gameState.imposterGuess && (
                    <button
                      onClick={handleReveal}
                      disabled={actionBusy}
                      className="w-full bg-green-600 hover:bg-green-500 disabled:bg-gray-600 px-4 py-3 rounded-lg font-bold"
                    >
                      Reveal Results
                    </button>
                  )}

                  {gameState.phase === 'finished' && (
                    <div className="space-y-3">
                      <div className="bg-purple-900/40 border border-purple-400/30 rounded-lg p-3">
                        <p className="text-sm text-purple-200 text-center mb-2">
                          🎉 Round Complete! Keep the party going?
                        </p>
                      </div>
                      <button
                        onClick={handleNewRound}
                        disabled={actionBusy}
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:bg-gray-600 px-4 py-3 rounded-lg font-bold text-lg shadow-lg"
                      >
                        🎮 Play Again
                      </button>
                      <p className="text-xs text-purple-300 text-center">
                        New word, new imposter, same players!
                      </p>
                      <button
                        onClick={handleEndGame}
                        disabled={actionBusy}
                        className="w-full bg-red-600/80 hover:bg-red-500 disabled:bg-gray-600 px-3 py-2 rounded-lg font-semibold text-sm"
                      >
                        End Game & Return to Party
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-400 text-center py-8">
                Waiting for game to start...
              </p>
            )}
          </div>

          {/* Right Column - Clues & Scores */}
          <div className="bg-white/10 rounded-xl p-6 border border-purple-400/30">
            <h2 className="text-2xl font-bold mb-4 text-purple-200">
              {gameState?.phase === 'reveal' || gameState?.phase === 'finished' ? 'Scores' : 'Clues'}
            </h2>
            
            {gameState && gameState.clues.length > 0 && (
              <div className="space-y-2 mb-6">
                {gameState.clues.map((clue, idx) => (
                  <div
                    key={idx}
                    className="bg-slate-900/40 rounded-lg p-3 border border-slate-400/30"
                  >
                    <p className="text-xs text-slate-400">
                      Round {clue.roundNumber} - {clue.playerId}
                    </p>
                    <p className="text-lg font-semibold text-purple-200">
                      "{clue.clue}"
                    </p>
                  </div>
                ))}
              </div>
            )}

            {gameState && (gameState.phase === 'reveal' || gameState.phase === 'finished') && (
              <div className="space-y-3">
                <h3 className="text-xl font-bold text-purple-300">Leaderboard</h3>
                {sortedScores.map((entry, idx) => (
                  <div
                    key={entry.name}
                    className={`rounded-lg p-3 border ${
                      idx === 0
                        ? 'bg-yellow-900/40 border-yellow-400/50'
                        : 'bg-slate-900/40 border-slate-400/30'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">
                        {idx + 1}. {entry.name}
                      </span>
                      <span className="text-2xl font-bold">{entry.score}</span>
                    </div>
                  </div>
                ))}

                {gameState.phase === 'reveal' && Object.keys(voteTallies).length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-xl font-bold text-purple-300 mb-2">Votes</h3>
                    {Object.entries(voteTallies).map(([player, votes]) => (
                      <div
                        key={player}
                        className="bg-red-900/30 rounded-lg p-3 border border-red-400/30 mb-2"
                      >
                        <div className="flex justify-between">
                          <span>{player}</span>
                          <span className="font-bold">{votes} votes</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQrModal && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          onClick={() => setShowQrModal(false)}
        >
          <div
            className="bg-white rounded-2xl p-8 max-w-md"
            onClick={e => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
              Join the Game
            </h2>
            <div className="bg-white p-4 rounded-lg mb-4">
              <QRCode value={joinUrl} size={256} className="mx-auto" />
            </div>
            <p className="text-center text-gray-600 text-sm mb-4">
              Scan to join on your phone
            </p>
            <button
              onClick={() => setShowQrModal(false)}
              className="w-full bg-purple-600 hover:bg-purple-500 text-white px-6 py-3 rounded-lg font-bold"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImposterHostPage;
